import { Marker } from "@adamscybot/react-leaflet-component-marker";
import { memo, useCallback } from "react";
import { isIOS, isMobile } from "react-device-detect";
import type MapEntityBase from "../../map-entities/map-entity-base";
import { Z_INDEX_OFFSETS } from "../constants";
import { DeviceMapItem } from "../types";
import { DeviceMapEntityIcon } from "./device-map-entity-icon";

interface DeviceMarkerProps {
    deviceMapItem: DeviceMapItem;
    currentMapZoom: number;
    isHovered: boolean;
    mapComponent: L.Map;
    onDeviceClick: (e: React.MouseEvent, mapEntity: MapEntityBase) => void;
    onIconHover: (id: string | null) => void;
    highlighted?: boolean;
    hidden?: boolean;
    isDimmed?: boolean;
}

export const DeviceMarkerMemo = memo(
    ({
        deviceMapItem,
        currentMapZoom,
        isHovered,
        mapComponent,
        onDeviceClick,
        onIconHover,
        highlighted,
        hidden,
        isDimmed,
    }: DeviceMarkerProps) => {
        const iconId = deviceMapItem.mapEntity.id ?? deviceMapItem.mapEntity.uid;

        const handleClick = useCallback(
            (e: React.MouseEvent) => onDeviceClick(e, deviceMapItem.mapEntity),
            [onDeviceClick, deviceMapItem.mapEntity],
        );

        const handleHover = useCallback(
            (e: React.MouseEvent, hovered: boolean) => {
                if (hovered && isMobile && isIOS) {
                    onDeviceClick(e, deviceMapItem.mapEntity);
                    // On iOS a tap fires hover-true then never fires hover-false (no mouse-leave).
                    // We dispatch the click to open the card and clear hover via handleDeviceClick.
                    // Returning here prevents onIconHover from re-setting hover, which would
                    // override the null-clear due to React batching both state updates together.
                    return;
                }
                onIconHover(hovered ? iconId : null);
            },
            [iconId, onDeviceClick, onIconHover, deviceMapItem.mapEntity],
        );

        return (
            <Marker
                key={iconId}
                position={[deviceMapItem.lat, deviceMapItem.long]}
                zIndexOffset={isHovered || highlighted ? Z_INDEX_OFFSETS.ICON_HOVERED : 0}
                icon={
                    <DeviceMapEntityIcon
                        currentMapZoom={currentMapZoom}
                        key={iconId}
                        mapComponent={mapComponent}
                        deviceModel={deviceMapItem.mapEntity.model}
                        onClickIcon={handleClick}
                        onHover={handleHover}
                        highlighted={highlighted}
                        dimmed={isDimmed}
                        hidden={hidden}
                    />
                }
            />
        );
    },
);
